import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import TextField from '@material-ui/core/TextField';
import { useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import { colors } from 'theme/color-palette';
import {
	Bogle_Regular,
	RENAME,
	UPDATE,
	SAVE_BUTTON,
	SAVE_DATASET,
	NAME_YOUR_DATASET,
	NAME_EXISTED,
	DONT_SAVE_2,
	SAVE_AS_NEW_DATASET,
	CDS_NAME_RESTRICTION_ERR,
	CUSTOM_DATASET_DEFAULT_NAME
} from 'constants/constant';
import ErrorIcon from '@material-ui/icons/Error';
import { DATASET_NAME_MAXLENGTH } from 'constants/constant';

const { LIGHT_GREY, PURPLE, WHITE, DARK_PURPLE } = colors;

const DATASET_NAME_REGEX = /^[a-zA-Z0-9 ]+$/;

const useStyles = makeStyles((theme) => ({
	root: {
		width: '100%',
		maxWidth: 360,
		backgroundColor: theme.palette.background.paper
	},
	paper: {
		width: '80%',
		maxHeight: 435,
		borderRadius: 16
	},
	cancelButton: {
		height: 40,
		fontWeight: '700',
		textTransform: 'none !important',
		borderRadius: '50px  !important',
		marginRight: '8px'
	},
	saveButton: {
		background: `${PURPLE}`,
		borderRadius: '50px  !important',
		height: 40,
		color: `${WHITE} !important`,
		fontSize: '14px',
		fontFamily: `${Bogle_Regular} !important`,
		textTransform: 'none !important',
		'&:hover': {
			background: `${DARK_PURPLE} !important`
		},
		'&.Mui-disabled': {
			color: `${WHITE} !important`,
			backgroundColor: `${LIGHT_GREY} !important`,
			border: 'none !important'
		}
	},
	dialogTitle: {
		'& h2': {
			fontSize: 24,
			fontWeight: 700
		}
	},
	dialogContent: {
		borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
		paddingBottom: 24,
		'& input': {
			borderRadius: 16,
			padding: '10px 12px',
			fontFamily: Bogle_Regular
		}
	},
	dialogAction: {
		padding: '8px 16px'
	},
	containedHelperText: {
		margin: '4px 0 0 0'
	},
	helperTextNode: {
		display: 'flex',
		alignItems: 'center'
	},
	helperText: {
		marginLeft: '4px'
	}
}));

const SaveDatasetModel = (props) => {
	const {
		isEditMode = false,
		previousName,
		onDontSave,
		isDuplicateName,
		open,
		onClose,
		onSaveModal
	} = props;
	const [datasetName, setDatasetName] = useState(() => {
		if (isEditMode) {
			return previousName;
		}
		return '';
	});
	const [duplicateNames, setDuplicateNames] = useState([]);
	const [restricedNameError, setRestricedNameError] = useState(false);
	const classes = useStyles();
	const { CDTourStepIndex } = useSelector((state) => state.tourReducer);
	const savedCustomDatasets = useSelector((state) => state.templateReducer.savedCustomDatasets);

	let shouldDisableSaveBtn = datasetName?.trim().length === 0;
	if (isEditMode && datasetName === previousName) {
		shouldDisableSaveBtn = true;
	}

	let count = 1;
	const getCDSName = (defaultCDSName, name, nameList) => {
		if (nameList.includes(name.toLowerCase())) {
			const defaultName = `${defaultCDSName} ${count}`;
			count = count + 1;
			return getCDSName(defaultCDSName, defaultName, nameList);
		}
		return name;
	};

	const getDatasetName = () => {
		const defaultCDSName = CUSTOM_DATASET_DEFAULT_NAME;
		let cdsNameList = savedCustomDatasets.map((a) => a.customDatasetName.toLowerCase());
		const cdsName = getCDSName(defaultCDSName, defaultCDSName, cdsNameList);
		setDatasetName(cdsName);
	};

	useEffect(() => {
		switch (CDTourStepIndex) {
			case 4:
				getDatasetName();
				break;
			case 5:
				onSave();
				break;
			default:
				break;
		}
	}, [CDTourStepIndex]);

	const onSave = () => {
		// Check if the entered name already exists in the database - if it does, then throw an error
		// otherwise, just do the backend call to save it
		const nameAlreadyExists = isDuplicateName(datasetName);
		if (nameAlreadyExists) {
			setDuplicateNames((currentState) => [...currentState, datasetName]);
			return;
		}

		onSaveModal(datasetName);
	};

	const onNameChange = (e) => {
		const newDatasetName = e.target.value;
		if (newDatasetName.length <= DATASET_NAME_MAXLENGTH) {
			setDatasetName(newDatasetName);
		}
	};

	const showError = duplicateNames.includes(datasetName);

	const getErrorMessage = (showError, restricedNameError) => {
		if (showError || restricedNameError) {
			return (
				<Box className={classes.helperTextNode}>
					<ErrorIcon />
					<span className={classes.helperText}>
						{showError ? NAME_EXISTED : CDS_NAME_RESTRICTION_ERR}
					</span>
				</Box>
			);
		}
		return null;
	};

	let dialogTitle = SAVE_DATASET;
	if (isEditMode) {
		dialogTitle = RENAME;
	}
	if (CDTourStepIndex === 4) {
		dialogTitle = SAVE_AS_NEW_DATASET;
	}

	return (
		<Dialog
			maxWidth="xs"
			style={{ zIndex: 99 }}
			aria-labelledby="save-dataset-title"
			open={open}
			classes={{
				paper: classes.paper
			}}
			id="save-dataset-dialog"
			keepMounted
			PaperProps={{ id: 'save-dataset-dialog-box' }}
			onClose={onClose}>
			<DialogTitle className={classes.dialogTitle} id="save-dataset-title">
				{dialogTitle}
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				<TextField
					placeholder={NAME_YOUR_DATASET}
					id="dataset-textfield"
					test-id="dataset-textfield"
					variant="outlined"
					size="small"
					value={datasetName}
					onChange={(e) => {
						const val = e?.target?.value;
						setRestricedNameError(!DATASET_NAME_REGEX.test(val));
						if (val !== '' && !DATASET_NAME_REGEX.test(val)) {
							return;
						}
						onNameChange(e);
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							onSave();
						}
					}}
					fullWidth={true}
					error={showError}
					helperText={getErrorMessage(showError, restricedNameError)}
					FormHelperTextProps={{
						classes: { contained: classes.containedHelperText }
					}}
				/>
			</DialogContent>
			<DialogActions>
				<Box className={classes.dialogAction}>
					<Button className={classes.cancelButton} onClick={onDontSave} variant="outlined">
						{DONT_SAVE_2}
					</Button>
					<Button
						id="saveButton"
						className={classes.saveButton}
						disabled={shouldDisableSaveBtn}
						onClick={onSave}
						variant="outlined">
						{isEditMode ? UPDATE : SAVE_BUTTON}
					</Button>
				</Box>
			</DialogActions>
		</Dialog>
	);
};

export default SaveDatasetModel;
